import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer } from "@phosphor-icons/react";

const photoSrc = (p) => (!p ? "" : p.startsWith("data:") || p.startsWith("http") ? p : `${API}/files/${p}?auth=${localStorage.getItem("token")}`);
const kids = (m) => (m.children?.length
  ? m.children.map((c) => `${c.full_name} (${c.sex || "—"}, ${c.date_of_birth || "—"})`).join("; ")
  : m.no_children === "yes" ? "None" : m.biological_children_names);

const SECTIONS = (m) => [
  ["Biodata", [["Title", m.title], ["First name", m.first_name], ["Middle name", m.middle_name], ["Last name", m.last_name],
    ["Sex", m.sex], ["Date of birth", m.date_of_birth], ["Age", m.age], ["Ghana Card", m.ghana_card_number], ["SSNIT number", m.ssnit_number]]],
  ["Contact", [["Contact number", m.contact_number], ["Alternate contact", m.alternate_contact_number], ["Email", m.email]]],
  ["Employment", [["Facility", m.facility_name], ["Facility type", m.facility_type], ["Town", m.town], ["District", m.district],
    ["Region", m.region_name], ["Professional grade", m.professional_grade], ["Cadre / rank", m.rank], ["Dues payment", m.pays_dues], ["T-shirt size", m.tshirt_size]]],
  ["Next of kin", [["Name", m.next_of_kin_name], ["Contact", m.next_of_kin_contact], ["Relationship", m.next_of_kin_relationship]]],
  ["Family", [["Marital status", m.marital_status], ["Spouse", m.spouse_name], ["Number of children", m.number_of_children], ["Children", kids(m)],
    ["Mother", [m.mother_name, m.mother_status].filter(Boolean).join(" — ")], ["Father", [m.father_name, m.father_status].filter(Boolean).join(" — ")]]],
  ["Emergency contact", [["Name", m.emergency_contact_name], ["Phone", m.emergency_contact_phone], ["Email", m.emergency_contact_email], ["Address", m.emergency_contact_address]]],
  ["Previous employment", [["Worked elsewhere", m.worked_at_previous_facility], ["Facility", m.previous_facility_name], ["Location", m.previous_facility_location], ["Type", m.previous_facility_type]]],
];

export default function PrintMember() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/members/${id}/admin-profile`).then((r) => setM(r.data.member)).catch(() => setError("This member could not be loaded."));
  }, [id]);
  if (error) return <p className="p-8 text-destructive" data-testid="print-member-error">{error}</p>;
  if (!m) return <p className="p-8 text-muted-foreground">Loading…</p>;
  const name = [m.title, m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");
  return (
    <div className="print-sheet mx-auto max-w-[820px] bg-white p-8 text-black" data-testid="print-member-sheet">
      <div className="mb-6 flex justify-end print:hidden">
        <Button className="gap-2" onClick={() => window.print()} data-testid="print-member-now"><Printer size={16} weight="bold" /> Print / Save as PDF</Button>
      </div>
      <header className="flex items-center justify-between gap-4 border-b-4 border-[hsl(var(--primary))] pb-4">
        <div className="flex items-center gap-3">
          <img src="/org_logo.png" alt="MELPWU logo" className="h-16 w-16 object-contain" />
          <div>
            <p className="font-display text-xl font-bold">MELPWU</p>
            <p className="text-xs uppercase tracking-[0.18em] text-neutral-600">Member Information Record</p>
          </div>
        </div>
        {m.profile_picture && <img src={photoSrc(m.profile_picture)} alt="Member" className="h-24 w-24 rounded object-cover border border-neutral-300" />}
      </header>
      <section className="mt-5 grid grid-cols-3 gap-4 text-sm">
        <div className="col-span-3"><p className="text-xs text-neutral-500">Full name</p><p className="text-lg font-bold" data-testid="print-member-name">{name}</p></div>
        <div><p className="text-xs text-neutral-500">Membership number</p><p className="font-mono font-semibold">{m.membership_id || "Not assigned"}</p></div>
        <div><p className="text-xs text-neutral-500">Staff ID</p><p className="font-mono font-semibold">{m.staff_id || "—"}</p></div>
        <div><p className="text-xs text-neutral-500">Status</p><p className="font-semibold capitalize">{(m.application_status || "").replace(/_/g, " ")}</p></div>
      </section>
      {SECTIONS(m).map(([title, rows]) => (
        <section key={title} className="mt-5 break-inside-avoid">
          <h2 className="border-b border-neutral-300 pb-1 text-xs font-bold uppercase tracking-[0.15em] text-[hsl(var(--primary))]">{title}</h2>
          <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="flex gap-2"><dt className="w-36 shrink-0 text-neutral-500">{label}</dt><dd className="font-medium">{value === 0 || value ? String(value) : "—"}</dd></div>
            ))}
          </dl>
        </section>
      ))}
      {m.education?.length > 0 && (
        <section className="mt-5 break-inside-avoid">
          <h2 className="border-b border-neutral-300 pb-1 text-xs font-bold uppercase tracking-[0.15em] text-[hsl(var(--primary))]">Education</h2>
          <ul className="mt-2 space-y-1 text-sm">{m.education.map((e, i) => <li key={i}><span className="font-medium">{e.qualification || "—"}</span>, {e.institution} {e.location && `(${e.location})`} {e.entry_year}–{e.graduation_year}</li>)}</ul>
        </section>
      )}
      <footer className="mt-8 border-t border-neutral-300 pt-3 text-[11px] text-neutral-500">Printed {new Date().toLocaleString()} · Confidential member record · MELPWU Members Portal</footer>
    </div>
  );
}
